/*
 * LicenseSure
 * https://licensesure.biz
 *
 * LicenseSure acquisition announcement component. Displays a dismissible pop-up three seconds after the visitor
 * arrives, at most once per browser session.
 */

// Core dependencies
import React, { useEffect, useRef, useState } from "react";

// Images
import LogoHarborCompliance from "../../../images/logoHarborCompliance.svg";
import logoLS from "../../../images/logoLS.png";

// Styles
import {
  actions,
  backdrop,
  button,
  close,
  dialog,
  eyebrow,
  link,
  logos,
} from "./announcement.module.css";
import {
  aligned,
  between,
  centered,
  flex,
  wrap,
} from "../../styles/global.module.css";

const classNames = require("classnames");

const CTA_URL = "https://www.harborcompliance.com/licensesure";
const FAQ_URL = "https://www.harborcompliance.com/licensesure#faqs";
const DELAY_MS = 3000;
/* Session storage key; holds the arrival timestamp until shown, then "shown". */
const STORAGE_KEY = "ls-acquisition-announcement";
const SHOWN = "shown";
const FOCUSABLE =
  "a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])";

/* In-memory fallback when sessionStorage is unavailable; survives client-side navigation within this page load. */
let memory = null;

function readState() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || memory;
  } catch (e) {
    return memory;
  }
}

function writeState(value) {
  memory = value;
  try {
    sessionStorage.setItem(STORAGE_KEY, value);
  } catch (e) {
    // Storage unavailable (e.g. private mode); in-memory fallback already set.
  }
}

export default function Announcement() {
  const [open, setOpen] = useState(false);
  const backdropRef = useRef(null);
  const dialogRef = useRef(null);

  /* Start the timer from the visitor's arrival on the site, not from this page's mount. Layout remounts on
   * client-side navigation, so persist the arrival time and count down the remaining delay. Skip if already shown
   * this session. Clean up if unmounted before the timer fires. */
  useEffect(() => {
    let state = readState();
    if (state === SHOWN) {
      return;
    }
    if (!state) {
      state = String(Date.now());
      writeState(state);
    }
    const remaining = Math.max(0, DELAY_MS - (Date.now() - Number(state)));
    const timer = setTimeout(() => {
      writeState(SHOWN);
      setOpen(true);
    }, remaining);
    return () => clearTimeout(timer);
  }, []);

  /* While open: lock page scroll, move focus into the dialog and trap it there; dismiss on Escape or a pointer press on
   * the backdrop. Restore scroll and focus on close. */
  useEffect(() => {
    if (!open) {
      return;
    }
    const html = document.documentElement;
    const previousOverflow = html.style.overflow;
    const previousFocus = document.activeElement;
    html.style.overflow = "hidden";
    dialogRef.current.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) {
        return;
      }
      const focusable = dialogRef.current.querySelectorAll(FOCUSABLE);
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const onContainer = document.activeElement === dialogRef.current;
      if (event.shiftKey && (document.activeElement === first || onContainer)) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || onContainer)
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    const onPointerDown = (event) => {
      if (event.target === backdropRef.current) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      html.style.overflow = previousOverflow;
      if (previousFocus) {
        previousFocus.focus();
      }
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className={classNames(backdrop, flex, aligned, centered)}
      ref={backdropRef}
    >
      <div
        aria-describedby="announcement-description"
        aria-labelledby="announcement-title"
        aria-modal="true"
        className={dialog}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <button
          aria-label="Dismiss announcement"
          className={close}
          onClick={() => setOpen(false)}
          type="button"
        >
          <i className={"material-icons-round"} aria-hidden="true">
            close
          </i>
        </button>
        <div className={classNames(logos, flex, aligned, between)}>
          <img src={logoLS} alt="LicenseSure" />
          <LogoHarborCompliance aria-label="Harbor Compliance" role="img" />
        </div>
        <span className={eyebrow}>Announcement</span>
        <h2 id="announcement-title">
          LicenseSure Is Now Part of Harbor Compliance
        </h2>
        <div id="announcement-description">
          <p>
            If you're a customer, your licensing and qualification services
            continue without interruption with the same pricing, and same
            specialists supporting you. There's nothing to do today.
          </p>
          <p>Not a customer and need services? Get a free consultation.</p>
        </div>
        <div className={classNames(actions, flex, aligned, wrap)}>
          <a className={button} href={CTA_URL}>
            Continue to Harbor Compliance
          </a>
          <a className={link} href={FAQ_URL}>
            Read the FAQs ›
          </a>
        </div>
      </div>
    </div>
  );
}
